import { useCallback, useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, lerpColor, fillStippleRect } from "./shared.jsx";

export const kind = "calendar";

// GitHub-style 7 × N calendar. We bucket data into per-day totals,
// then walk the most recent `weeks` weeks back from today, drawing a
// shaded cell per day. Weeks default to 26 (≈ 6 months) which fits
// the dashboard's typical surface.
export function Component({ spec, data }) {
    const grid = useMemo(() => buildCalendar(spec, data), [spec, data]);
    const color = spec.color || "var(--green)";
    const weeks = Math.max(4, Math.min(104, Number(spec.weeks) || 26));
    const height = 7 * 14 + 24; // 7 rows × cell + label strip

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!grid) return;
        const { byDay, max } = grid;
        const fg = resolveCssColor(root, color) || "#5a8c5e";
        const bg = resolveCssColor(root, "var(--plot-bg)") || "#1a1a1a";
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";

        const padL = 22;
        const padT = 16;
        const padR = 4;
        const padB = 4;
        const cellW = Math.max(4, Math.floor((width - padL - padR) / weeks));
        const cellH = Math.max(4, Math.floor((h - padT - padB) / 7));
        const gap = 2;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Anchor the rightmost column on today's weekday so the most
        // recent cells sit flush right. We walk back `weeks` × 7 days.
        const totalDays = weeks * 7;
        const start = new Date(today);
        start.setDate(start.getDate() - (totalDays - 1));
        // Snap start to a Sunday so each column is a clean week.
        const startWeekday = start.getDay(); // 0..6
        start.setDate(start.getDate() - startWeekday);
        const adjustedTotalDays = totalDays + startWeekday;
        const adjustedWeeks = Math.ceil(adjustedTotalDays / 7);

        for (let i = 0; i < adjustedTotalDays; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            if (d > today) continue;
            const wk = Math.floor(i / 7);
            const wd = i % 7;
            const key = isoDate(d);
            const v = byDay.get(key) ?? 0;
            const t = max > 0 ? v / max : 0;
            const x = padL + (adjustedWeeks - 1 - wk) * cellW;
            const y = padT + wd * cellH;
            const rx = x + gap / 2;
            const ry = y + gap / 2;
            const rw = Math.max(0, cellW - gap);
            const rh = Math.max(0, cellH - gap);
            if (t > 0) {
                ctx.fillStyle = lerpColor(bg, fg, 0.08 + t * 0.28);
                ctx.fillRect(rx, ry, rw, rh);
                fillStippleRect(ctx, rx, ry, rw, rh, fg, {
                    size: 4,
                    dotRadius: 0.8,
                    density: 0.25 + t * 0.75,
                });
            }
        }

        // weekday labels (Mon / Wed / Fri)
        ctx.font = "9px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.fillStyle = muted;
        ctx.textBaseline = "middle";
        ctx.textAlign = "right";
        const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
        for (let wd = 0; wd < 7; wd++) {
            if (wd !== 1 && wd !== 3 && wd !== 5) continue;
            ctx.fillText(dayLabels[wd], padL - 4, padT + wd * cellH + cellH / 2);
        }

        // month strip across the top — render labels at week boundaries
        // where the month changes.
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        let lastMonth = -1;
        for (let wk = 0; wk < adjustedWeeks; wk++) {
            const d = new Date(start);
            d.setDate(start.getDate() + wk * 7);
            const m = d.getMonth();
            if (m !== lastMonth) {
                lastMonth = m;
                const x = padL + (adjustedWeeks - 1 - wk) * cellW;
                ctx.fillStyle = muted;
                ctx.fillText(MONTHS[m], x, padT - 4);
            }
        }
    }, [grid, color, weeks]);

    if (!grid || grid.byDay.size === 0) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    return (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]">
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
                    {spec.title}
                </div>
            ) : null}
            <CanvasSurface
                draw={draw}
                deps={[grid, color, weeks]}
                height={height}
                className="w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]"
            />
            {spec.format ? (
                <div className="mt-[4px] text-[10px] text-[color:var(--muted-soft)] text-right tabular-nums">
                    max {formatValue(grid.max, spec.format)}/day
                </div>
            ) : null}
        </div>
    );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildCalendar(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    if (!arr.length) return null;
    const dateField = spec.dateField;
    const valueField = spec.valueField;
    if (!dateField || !valueField) return null;
    const byDay = new Map();
    let max = 0;
    for (const row of arr) {
        if (!row) continue;
        const raw = row[dateField];
        const v = asNumber(row[valueField]) ?? 0;
        const key = parseToIsoDate(raw);
        if (!key) continue;
        const prev = byDay.get(key) ?? 0;
        const next = prev + v;
        byDay.set(key, next);
        if (next > max) max = next;
    }
    return { byDay, max };
}

function parseToIsoDate(v) {
    if (v == null) return null;
    let d;
    if (typeof v === "number") d = new Date(v);
    else if (typeof v === "string") d = new Date(v);
    else return null;
    if (Number.isNaN(d.getTime())) return null;
    return isoDate(d);
}

function isoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
